import { useState } from "react";
import { NavLink } from "react-router-dom";
import { UserButton } from "@clerk/react";
import { LayoutDashboard, Users, Stethoscope, Baby, CalendarDays, ShieldCheck, Menu, X, ClipboardList, Activity, BookMarked, RefreshCw, KeyRound, BookOpen, Sparkles, Trash2 } from "lucide-react";
import { useMe, can } from "@/context/MeContext";
import { ROLE_LABELS } from "@/lib/api";

const NAV = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, id: "dashboard", end: true },
  { to: "/utilisateurs", label: "Utilisateurs", icon: Users, id: "users" },
  { to: "/enfants", label: "Enfants", icon: Baby, id: "patients" },
  { to: "/medecins", label: "Praticiens", icon: Stethoscope, id: "doctors" },
  { to: "/annuaire", label: "Annuaire", icon: BookOpen, id: "directory" },
  { to: "/rendez-vous", label: "Rendez-vous", icon: CalendarDays, id: "appointments" },
  { to: "/dossiers", label: "Dossiers médicaux", icon: ClipboardList, id: "records" },
  { to: "/epidemies", label: "Épidémies", icon: Activity, id: "epidemics" },
  { to: "/assistant-ia", label: "Assistant IA", icon: Sparkles, id: "ai", perm: "ai" },
  { to: "/referentiels", label: "Référentiels", icon: BookMarked, id: "referentials" },
  { to: "/synchronisation", label: "Synchronisation", icon: RefreshCw, id: "sync" },
  { to: "/administrateurs", label: "Administrateurs", icon: ShieldCheck, id: "accounts", perm: "accounts" },
  { to: "/cles-api", label: "Clés API", icon: KeyRound, id: "api-keys", perm: "accounts" },
  { to: "/corbeille", label: "Corbeille", icon: Trash2, id: "trash", perm: "delete" },
];

const Brand = () => (
  <div className="flex items-center gap-3 px-6 py-6">
    <div className="h-9 w-9 rounded-md bg-[#C2410C] text-white grid place-items-center font-heading font-black">S</div>
    <div>
      <p className="font-heading font-extrabold tracking-tight text-stone-900 leading-none">Sehatic-Pedia</p>
      <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500 mt-1">Administration</p>
    </div>
  </div>
);

const NavItems = ({ onNavigate }) => {
  const me = useMe();
  return (
    <nav className="px-3 space-y-1">
      {NAV.filter((n) => !n.perm || can(me, n.perm)).map(({ to, label, icon: Icon, id, end }) => (
        <NavLink key={to} to={to} end={end} onClick={onNavigate} data-testid={`nav-${id}`}
          className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors duration-200 ${isActive ? "bg-[#C2410C] text-white" : "text-stone-600 hover:bg-stone-100 hover:text-stone-900"}`}>
          <Icon className="h-4 w-4" strokeWidth={1.8} />
          {label}
        </NavLink>
      ))}
    </nav>
  );
};

export default function Layout({ children }) {
  const me = useMe();
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-[#F5F5F4] flex">
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-stone-200 bg-white sticky top-0 h-screen" data-testid="sidebar">
        <Brand />
        <NavItems />
        <p className="mt-auto px-6 py-6 text-xs text-stone-400">Plateforme de gestion de l'app mobile</p>
      </aside>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-64 bg-white border-r border-stone-200" data-testid="mobile-sidebar">
            <button className="absolute right-3 top-6 p-1 text-stone-500" onClick={() => setOpen(false)} data-testid="mobile-menu-close"><X className="h-5 w-5" /></button>
            <Brand />
            <NavItems onNavigate={() => setOpen(false)} />
          </aside>
        </div>
      )}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-30 bg-white border-b border-stone-200 h-16 px-4 sm:px-8 flex items-center gap-4">
          <button className="lg:hidden p-2 -ml-2 text-stone-700" onClick={() => setOpen(true)} data-testid="mobile-menu-open"><Menu className="h-5 w-5" /></button>
          <div className="ml-auto flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium text-stone-900" data-testid="header-user-name">{me.name || me.email}</p>
              <p className="text-xs text-[#C2410C]" data-testid="header-user-role">{ROLE_LABELS[me.role]}</p>
            </div>
            <div data-testid="user-menu"><UserButton /></div>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-8 lg:p-10">{children}</main>
      </div>
    </div>
  );
}
